import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const types = [
  { v: "denim", l: "Denim Customisation" },
  { v: "vinyl", l: "Vinyl Press" },
  { v: "mixed", l: "Mixed Media" },
  { v: "creative_day", l: "Creative Day Workshop" },
  { v: "party", l: "Party Package" },
];
const ages = [
  { v: "child", l: "Child (8–11)" },
  { v: "teen", l: "Teen (12–17)" },
  { v: "adult", l: "Adult" },
  { v: "mixed", l: "Mixed group" },
];

const DEPOSIT = 10;

export default function BookingForm({ defaultType = "denim", workshopId = "" }) {
  const [form, setForm] = useState({
    parent_name: "", child_name: "", child_age: "",
    email: "", phone: "", emergency_contact: "",
    workshop_id: workshopId,
    workshop_type: defaultType, age_group: "child",
    preferred_date: "", participants: 1,
    allergies: "", message: "",
    agree_terms: false,
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const total = DEPOSIT * Math.max(1, Number(form.participants) || 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_terms) {
      toast.error("Please accept the deposit terms to continue.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        booking: {
          parent_name: form.parent_name,
          name: form.parent_name,
          email: form.email,
          phone: form.phone,
          emergency_contact: form.emergency_contact,
          workshop_id: form.workshop_id || "",
          workshop_type: form.workshop_type,
          age_group: form.age_group,
          preferred_date: form.preferred_date,
          participants: Number(form.participants) || 1,
          child_name: form.child_name,
          child_age: form.child_age,
          allergies: form.allergies,
          message: form.message,
        },
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/bookings/checkout", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-7">
      <div className="border-2 border-rose bg-rose/10 p-5 flex gap-3 items-start" style={{ boxShadow: "4px 4px 0 #FF2A70" }}>
        <ShieldCheck size={22} className="text-rose flex-shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <p className="font-display text-xl uppercase text-rose tracking-wide">Deposit required to secure booking</p>
          <p className="text-white/80 mt-1">
            A £{DEPOSIT} non-refundable deposit per attendee is taken at the time of
            booking. The remaining balance is paid on the day. Suitable for all ages —
            adults, teens, parents and children all welcome.
          </p>
        </div>
      </div>

      <div>
        <p className="overline mb-4">Your details</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Your name</span>
            <input data-testid="booking-parent-name" required value={form.parent_name} onChange={update("parent_name")} className="input-brutal mt-2" placeholder="Booking contact" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Email</span>
            <input data-testid="booking-email" type="email" required value={form.email} onChange={update("email")} className="input-brutal mt-2" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Phone</span>
            <input data-testid="booking-phone" required value={form.phone} onChange={update("phone")} className="input-brutal mt-2" placeholder="07…" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Emergency contact <span className="normal-case tracking-normal text-white/40 font-normal">(recommended for under-18s)</span></span>
            <input data-testid="booking-emergency" value={form.emergency_contact} onChange={update("emergency_contact")} className="input-brutal mt-2" placeholder="Name & phone" />
          </label>
        </div>
      </div>

      <div>
        <p className="overline mb-4">Attendee</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Attendee name</span>
            <input data-testid="booking-child-name" required value={form.child_name} onChange={update("child_name")} className="input-brutal mt-2" placeholder="Who's coming?" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Age</span>
            <input data-testid="booking-child-age" required value={form.child_age} onChange={update("child_age")} className="input-brutal mt-2" placeholder="e.g. 9 or adult" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Allergies / medical info</span>
            <textarea data-testid="booking-allergies" rows={2} value={form.allergies} onChange={update("allergies")} className="input-brutal mt-2 resize-none" placeholder="Anything we should know" />
          </label>
        </div>
      </div>

      <div>
        <p className="overline mb-4">Workshop</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Workshop type</span>
            <select data-testid="booking-type" value={form.workshop_type} onChange={update("workshop_type")} className="input-brutal mt-2">
              {types.map((t) => <option key={t.v} value={t.v} className="bg-coal">{t.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Age group</span>
            <select data-testid="booking-age" value={form.age_group} onChange={update("age_group")} className="input-brutal mt-2">
              {ages.map((t) => <option key={t.v} value={t.v} className="bg-coal">{t.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Preferred date</span>
            <input data-testid="booking-date" type="text" value={form.preferred_date} onChange={update("preferred_date")} className="input-brutal mt-2" placeholder="e.g. Tuesday 28th July 2026" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Number of attendees</span>
            <input data-testid="booking-participants" type="number" min="1" value={form.participants} onChange={update("participants")} className="input-brutal mt-2" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold">Anything else?</span>
            <textarea data-testid="booking-message" rows={3} value={form.message} onChange={update("message")} className="input-brutal mt-2 resize-none" placeholder="Theme, vibe, anything we should know…" />
          </label>
        </div>
      </div>

      <div className="border-2 border-white p-5 flex flex-wrap items-center justify-between gap-4 bg-ink">
        <div>
          <p className="overline">Deposit due now</p>
          <p className="font-display text-5xl text-gold mt-1">£{total.toFixed(2)}</p>
          <p className="text-[11px] tracking-wider uppercase text-white/60 mt-1">£{DEPOSIT} × {Math.max(1, Number(form.participants) || 1)} attendee{Math.max(1, Number(form.participants) || 1) > 1 ? "s" : ""}</p>
        </div>
        <label className="flex items-start gap-3 max-w-md text-sm text-white/80 cursor-pointer">
          <input type="checkbox" data-testid="booking-terms" checked={form.agree_terms} onChange={update("agree_terms")} className="mt-1 accent-[#FF2A70] w-4 h-4" />
          <span>I agree — <span className="text-rose font-bold">deposits are non-refundable unless the workshop is cancelled by us.</span></span>
        </label>
      </div>

      <div className="pt-2">
        <button data-testid="booking-submit" disabled={busy} className="btn-accent disabled:opacity-50">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : `Pay £${total.toFixed(2)} & secure spot`}
        </button>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-3">Secure payment · Powered by Stripe</p>
      </div>
    </form>
  );
}
